import { useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Activity,
  CalendarDays,
  BarChart3,
  UserCog,
  LogOut,
  Menu,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";

const PAGE_TITLES = [
  { match: (p) => p === "/", title: "Panel de Órdenes" },
  { match: (p) => p === "/orders/new", title: "Nueva Orden de Servicio" },
  { match: (p) => /^\/orders\/[^/]+\/edit$/.test(p), title: "Editar Orden" },
  { match: (p) => /^\/orders\/[^/]+$/.test(p), title: "Detalle de Orden" },
  { match: (p) => p === "/clients", title: "Clientes" },
  { match: (p) => /^\/clients\/[^/]+$/.test(p), title: "Detalle del Cliente" },
  { match: (p) => p === "/monitor", title: "Monitoreo de Red" },
  { match: (p) => p === "/visits", title: "Calendario de Visitas" },
  { match: (p) => p === "/reports", title: "Reportes y Métricas" },
  { match: (p) => p === "/users", title: "Gestión de Usuarios" },
];

function NavItems({ onNavigate }) {
  const { user } = useAuth();
  const items = [
    { to: "/", label: "Panel Principal", icon: LayoutDashboard, testId: "nav-dashboard", end: true },
    { to: "/clients", label: "Clientes", icon: Building2, testId: "nav-clients" },
    { to: "/monitor", label: "Monitoreo", icon: Activity, testId: "nav-monitor" },
    { to: "/visits", label: "Visitas", icon: CalendarDays, testId: "nav-visits" },
    { to: "/reports", label: "Reportes", icon: BarChart3, testId: "nav-reports" },
  ];
  if (user?.role === "admin") {
    items.push({ to: "/users", label: "Usuarios", icon: UserCog, testId: "nav-users" });
  }
  return (
    <nav className="flex-1 space-y-1 px-3 py-4">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-testid={item.testId}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
              isActive
                ? "bg-primary text-white"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`
          }
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center justify-center border-b border-slate-200 px-4 py-5">
      <img
        src="/logo.png"
        alt="SoloSoporte"
        className="h-11 w-auto max-w-full object-contain"
        data-testid="brand-logo"
      />
    </div>
  );
}

function UserFooter() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };
  return (
    <div className="border-t border-slate-200 p-4">
      <div className="mb-2">
        <p className="truncate text-sm font-semibold text-slate-900" data-testid="sidebar-user-name">{user?.name}</p>
        <p className="truncate text-xs text-slate-500">{user?.email}</p>
        <span className="mt-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 border border-blue-200" data-testid="sidebar-user-role">
          {user?.role === "admin" ? "Administrador" : "Técnico"}
        </span>
      </div>
      <Button
        variant="outline"
        size="sm"
        data-testid="logout-button"
        onClick={handleLogout}
        className="w-full justify-start gap-2 text-slate-600"
      >
        <LogOut className="h-4 w-4" />
        Cerrar sesión
      </Button>
    </div>
  );
}

export function Layout() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const page = PAGE_TITLES.find((p) => p.match(location.pathname));
  const title = page ? page.title : "SoloSoporte IT";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col bg-white border-r border-slate-200 md:flex">
        <Brand />
        <NavItems />
        <UserFooter />
      </aside>

      <div className="md:pl-64">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-4 md:px-6">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex w-64 flex-col p-0">
              <Brand />
              <NavItems onNavigate={() => setMobileOpen(false)} />
              <UserFooter />
            </SheetContent>
          </Sheet>
          <h1 className="font-heading text-lg font-bold tracking-tight text-slate-900" data-testid="page-title">
            {title}
          </h1>
        </header>
        <main className="p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
